<template>
  <div class="feedback">
    <DashboardTitleSearch :property-id="property_id" :category-id="cat_id" />

    <div v-if="feedbacks.data" class="dashboard">
      <div>
        <h1>{{ stats.name }} Analysis</h1>

        <span v-if="!feedbacks.data">No Feedback!</span>

        <div class="full-box header-boxes">
          <div class="dashboard-box-horizontal four-lines">
            <div class="box-number">#{{ stats.rank }}</div>
            <span class="box-header space-5">Impact Rank</span>
            <div class="box-number">{{ stats.satisfaction_impact }}%</div>
            <span class="box-header">Satisfaction Impact</span>
          </div>
          <div class="dashboard-box-horizontal">
            <div class="">
              <div
                v-if="stats.sentiment_score <= -11"
                class="sentiment-icons2 space-5"
              >
                <img src="../assets/mad.png" />
              </div>
              <div
                v-else-if="
                  stats.sentiment_score > -11 && stats.sentiment_score < 49
                "
                class="sentiment-icons2 space-5"
              >
                <img src="../assets/neutral.png" />
              </div>
              <div
                v-else-if="stats.sentiment_score > 49"
                class="sentiment-icons2 space-5"
              >
                <img src="../assets/happy.png" />
              </div>
            </div>
            <br />
            <span class="box-number space-5" style="align:left; color:green;"
              >{{
                (
                  (stats.positive_sentiment_count / stats.total_count) *
                  100
                ).toFixed(1)
              }}%</span
            >
            Positive Sentiment<br />
            <span class="box-number"
              >{{
                (
                  (stats.negative_sentiment_count / stats.total_count) *
                  100
                ).toFixed(1)
              }}%</span
            >
            Negative Sentiment
          </div>
          <div class="dashboard-box-horizontal">
            <span class="box-number">{{ stats.total_count }}</span>
            <span class="box-header">Total Helpful Feedback</span>
            <span class="box-footer">past 6 weeks</span>
          </div>
        </div>

        <div v-if="cat_summary_action" class="full-body">
          <h2 class="bold">KEY ACTION SUMMARY</h2>
          <div class="strategic-action">
            <span class="action-text">{{ cat_summary_action }}</span>
          </div>
        </div>
        <br />

        <div class="full-body">
          <h2 class="bold">STRATEGIC ACTION RECOMMENDATION</h2>
          <div class="strategic-action">
            <span class="action-text">{{ action.action_name }}</span>
          </div>
        </div>

        <br />

        <h2 class="bold">KEY PHRASES</h2>
        <div class="ngrams">
          <div class="ngrams-box2">
            <span v-if="!allgrams.data">No Results</span>
            <div
              v-for="(quadgram, index) in allgrams.data"
              :key="quadgram.word_id"
              :class="['dashboard-box', getSentimentClass(quadgram.mcss)]"
            >
              <div class="dashboard-box2-left">
                <span class="big-digits-number2">{{ index + 1 }}</span
                ><br />
              </div>
              <div class="dashboard-box2-right">
                <div v-if="quadgram.mcss <= -11" class="sentiment-icons2">
                  <img src="../assets/mad.png" />
                </div>
                <div
                  v-else-if="quadgram.mcss > -11 && quadgram.mcss < 49"
                  class="sentiment-icons2"
                >
                  <img src="../assets/neutral.png" />
                </div>
                <div v-else-if="quadgram.mcss > 49" class="sentiment-icons2">
                  <img src="../assets/happy.png" />
                </div>
              </div>
              <div class="box-header-small-line category-body">
                <span class="box-link">
                  <router-link
                    :to="{
                      name: 'dashboardFeedbackByCategoryByWords',
                      params: {
                        id: property_id,
                        cat_id: cat_id,
                        word_id: quadgram.word_id
                      }
                    }"
                  >
                    "{{ quadgram.words }}" </router-link
                  ><br />
                </span>
                <span class="box-number-small"
                  >{{
                    Math.round(
                      (quadgram.action / allgrams.action_score_total) * 100
                    )
                  }}% </span
                >
                <span>Satisfaction Impact</span> <br />
                <span class="box-number-small">{{ quadgram.mcss }} </span>
                <span>Recent Experience Score</span><br />
                <span class="box-number-small">{{ quadgram.count }} </span>
                <span>Frequency</span>
              </div>
            </div>
          </div>
          <div class="clearfix" />
        </div>
      </div>
      <div class="full-box">
        <h2 class="bold">
          MOST COMMON {{ cat_name.toUpperCase() }} CUSTOMER PHRASES
        </h2>
        <div class="word-cloud">
          <vue-word-cloud
            style="
              height: 450px;
              width: 100%;
            "
            :words="wordcloud.data"
            :color="
              ([, weight, sentiment]) =>
                sentiment > 49
                  ? '#00a660'
                  : sentiment > -11
                  ? '#929292'
                  : '#de4c40'
            "
            :spacing="0.8"
            :animation-duration="5"
            :animation-overlap="5"
            animation-easing="ease-in-out"
            font-family="Merienda"
          >
            <template v-slot="props">
              <div style="cursor: pointer;" @click="onWordClick(props.text)">
                {{ props.text }}
              </div>
            </template>
          </vue-word-cloud>
        </div>
      </div>

      <div class="full-box">
        <div class="dashboard-graph-line center">
          <h2 class="bold">
            RECENT EXPERIENCE SCORE TREND - {{ cat_name.toUpperCase() }}
          </h2>
          <div v-if="sentimentLineChartOptions" style="padding: 5px;">
            <apexchart
              type="area"
              height="350"
              :options="sentimentLineChartOptions"
              :series="sentimentLineChartSeries"
            ></apexchart>
          </div>
        </div>
        <div class="dashboard-graph-doughnut center">
          <div>
            <h2 class="bold">RECENT EXPERIENCE SENTIMENT ANALYSIS</h2>
          </div>
          <div v-if="pieExperienceSeries" class="pie-chart">
            <apexchart
              type="pie"
              :options="pieExperienceOptions"
              :series="pieExperienceSeries"
            ></apexchart>
          </div>
        </div>
      </div>

      <h2 class="bold">FEEDBACK</h2>
      <div class="feedback-list">
        <div
          v-if="Math.ceil(feedbacks.total_items / per_page) >= page"
          class="pagination"
        >
          <button
            v-if="page > 1"
            class="button button-100 page-back"
            @click="pageBack"
          >
            Back
          </button>
          &nbsp;
          <div class="pagination-page">
            Page {{ page }} of {{ Math.ceil(feedbacks.total_items / per_page) }}
          </div>
          <button
            v-if="page < Math.ceil(feedbacks.total_items / per_page)"
            class="button button-100 page-next"
            @click="pageNext"
          >
            Next
          </button>
        </div>
        <div class="clearfix" />
        <div
          v-for="(feedback, i) in feedbacks.data"
          :key="i"
          class="feedback-item"
        >
          <FeedbackItem :feedback="feedback" />
        </div>
      </div>
      <div
        v-if="Math.ceil(feedbacks.total_items / per_page) >= page"
        class="pagination"
      >
        <button
          v-if="page > 1"
          class="button button-100 page-back"
          @click="pageBack"
        >
          Back
        </button>
        &nbsp;
        <div class="pagination-page">
          Page {{ page }} of {{ Math.ceil(feedbacks.total_items / per_page) }}
        </div>
        <button
          v-if="page < Math.ceil(feedbacks.total_items / per_page)"
          class="button button-100 page-next"
          @click="pageNext"
        >
          Next
        </button>
      </div>
    </div>

    <div v-else>
      <h1>{{ stats.name }} Analysis</h1>
      No Feedback!
    </div>
  </div>
</template>

<script>
import DashboardTitleSearch from "@/components/DashboardTitleSearch";
import FeedbackItem from "@/components/FeedbackItem";
import VueWordCloud from "vuewordcloud";
import api from "@/api/index";
const apiService = new api.APIServiceFeedbackDashboard();

export default {
  name: "DashboardFeedbackByCategory",
  components: {
    DashboardTitleSearch,
    FeedbackItem,
    [VueWordCloud.name]: VueWordCloud
  },
  data() {
    return {
      feedbacks: [],
      action: {},
      stats: {},
      allgrams: [],
      page: 1,
      per_page: 25,
      total_items: 0,
      total_pages: 0,
      wordcloud: [],
      property_id: this.$route.params.id,
      cat_id: parseInt(this.$route.params.cat_id),
      cat_name: "",
      cat_summary_action: "",
      ngram_action_score_total: 0,
      pieExperienceOptions: {
        chart: {
          width: "100%",
          type: "pie"
        },
        legend: {
          position: "top"
        },
        labels: ["Positive", "Neutral", "Negative"],
        colors: ["#00a660", "#cfd1d3", "#de4c40"]
      },
      pieExperienceSeries: [],
      sentimentLineChartOptions: {},
      sentimentLineChartSeries: []
    };
  },
  created() {
    this.getPropertyFeedbackActionByCategory();
    this.getPropertyFeedbackByCategory();
    this.getPropertyCategoryWordCloudAllgram();
    this.getWordCloud();
    this.getPropertyFeedbackCategoryStats();
    this.getPropertyFeedbackCategoryNPSWeekly();
  },
  methods: {
    getPropertyFeedbackActionByCategory: function() {
      apiService
        .getPropertyFeedbackActionByCategory(
          this.$route.params.id,
          this.$route.params.cat_id
        )
        .then(data => {
          this.action = data.data;
        });
    },
    getPropertyFeedbackSummaryDashboardHome: function () {
      apiService
        .getFeedbackSummaryDashboardHome(this.$route.params.id)
        .then(data => {
          this.feedback_summary = data.data.json.categories
          // Searching for a match
          let currentCategory = this.cat_name.toLowerCase();
           console.log("currentCategory")
          console.log(currentCategory)
          let matchingCategory = this.feedback_summary.find(category => category.cat_name.toLowerCase() === currentCategory);
          console.log("feedback_summary")
          console.log(data.data.json.categories)
          if (matchingCategory) {
            // Do something with the matchingCategory
            this.cat_summary_action = matchingCategory.cat_action
            console.log("Found matching category:", matchingCategory);
          } else {
            console.log("No matching category found");
          }
        });
    },
    getPropertyFeedbackByCategory: function() {
      apiService
        .getPropertyFeedbackByCategory(
          this.$route.params.id,
          this.$route.params.cat_id,
          this.page
        )
        .then(data => {
          this.feedbacks = data;
        });
    },
    getPropertyCategoryWordCloudAllgram: function() {
      apiService
        .getPropertyCategoryWordCloudAllgram(
          this.$route.params.id,
          this.$route.params.cat_id
        )
        .then(data => {
          this.allgrams = data;
        });
    },
    getWordCloud: function() {
      apiService
        .getPropertyWordCloudByCategoryForWordCloudGraphic(
          this.$route.params.id,
          this.$route.params.cat_id
        )
        .then(data => {
          this.wordcloud = data;
        });
      window.dispatchEvent(new Event("resize"));
    },
    getPropertyFeedbackCategoryStats: function() {
      apiService
        .getPropertyFeedbackCategoryStats(
          this.$route.params.id,
          this.$route.params.cat_id
        )
        .then(data => {
          this.stats = data;
          this.cat_name = data.name;
          this.getPropertyFeedbackSummaryDashboardHome()
          this.pieExperienceSeries = [
            data.positive_sentiment_count,
            data.neutral_sentiment_count,
            data.negative_sentiment_count
          ];
          this.$store.commit("setProperty", {
            id: this.$route.params.id,
            name: data.property_name
          });
        });
    },
    getPropertyFeedbackCategoryNPSWeekly: function() {
      apiService
        .getPropertyFeedbackCategoryNPSWeekly(
          this.$route.params.id,
          this.$route.params.cat_id
        )
        .then(data => {
          this.sentimentLineChartOptions = {
            chart: {
              type: "area",
              zoom: {
                enabled: false
              }
            },
            dataLabels: {
              enabled: false
            },
            stroke: {
              curve: "smooth"
            },
            yaxis: {
              min: -100,
              max: 100
            },
            grid: {
              row: {
                colors: ["#f3f3f3", "transparent"],
                opacity: 0.5
              }
            },
            xaxis: {
              labels: {
                rotate: -45
              },
              tickPlacement: "on",
              categories: data.nps_week_num
            }
          };
          this.sentimentLineChartSeries = [
            {
              name: "Experience Score",
              data: data.machinecore_nps_week_value_list
            }
          ];
        });
    },
    onWordClick: function(theword) {
      console.log("clicked");
    },
    pageBack: function() {
      this.page = this.page - 1;
      this.getPropertyFeedbackByCategory();
    },
    pageNext: function() {
      this.page = this.page + 1;
      this.getPropertyFeedbackByCategory();
    },
    getSentimentClass(score) {
      var sentimentColor = "";
      if (score <= -11 && score >= -100) {
        sentimentColor = "negative-color";
      } else if (score > -11 && score <= 49) {
        sentimentColor = "neutral-color";
      } else if (score > 49 && score <= 100) {
        sentimentColor = "positive-color";
      } else {
        sentimentColor = "empty-color";
      }
      return sentimentColor;
    }
  }
};
</script>

<style scoped>
h1 {
  font-size: 32px;
}
#feedback {
  margin: 0 0;
}
.dashboard {
  lost-utility: clearfix;
}
.dashboard-box-horizontal {
  lost-column: 1/3;
  height: 160px;
  background-color: #ffffff;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05), 0 5px 15px 0 rgba(0, 0, 0, 0.05);
  text-align: center;
  vertical-align: middle;
  line-height: 30px;
  padding: 28px 0;
  margin-bottom: 10px;
  border-radius: 4px;
  border: 1px solid #cfd1d3;
}
.two-rows {
  padding-top: 40px;
}
.dashboard-box-horizontal .box-header {
  font-size: 21px;
  display: block;
}
.dashboard-box-horizontal .box-number {
  font-size: 30px;
  font-weight: bold;
  color: #c03131;
}
.dashboard-box-horizontal .box-footer {
  font-size: 14px;
  display: block;
}
.space-5 {
  padding-bottom: 5px;
  margin-bottom: 5px;
}
.box-number-small {
  font-size: 19px;
}
.four-lines {
  padding: 16px;
}
.action-text {
  font-size: 24px;
}
.feedback-list {
  lost-utility: clearfix;
}
.pagination {
  float: right;
  margin-bottom: 10px;
}
.pagination-page {
  padding-top: 12px;
  padding-right: 5px;
  float: left;
}
.ngrams {
  padding: 0 3px 0 10px;
  margin-bottom: 20px;
}
.ngram {
  lost-column: 1/3;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05), 0 5px 15px 0 rgba(0, 0, 0, 0.05);
  background-color: #ffffff;
  border: 1px solid #cfd1d3;
  padding: 6px 0;
  margin-bottom: 10px;
  border-radius: 4px;
}
.ngram span {
  font-size: 24px;
}
.dashboard-graph-line {
  lost-column: 2/3;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05), 0 5px 15px 0 rgba(0, 0, 0, 0.05);
  background-color: #ffffff;
  border: 1px solid #cfd1d3;
  padding-top: 14px;
  border-radius: 4px;
  height: 410px;
}
.dashboard-graph-doughnut {
  lost-column: 1/3;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05), 0 5px 15px 0 rgba(0, 0, 0, 0.05);
  background-color: #ffffff;
  border: 1px solid #cfd1d3;
  padding-top: 14px;
  border-radius: 4px;
  height: 410px;
  max-height: 410px;
}
.pie-chart {
  padding: 5px;
  max-width: 410px;
  margin: 0 auto;
}
.feedback-item {
  lost-column: 1/2;
}
.dashboard-box {
  lost-column: 1/3;
  height: 144px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05), 0 5px 15px 0 rgba(0, 0, 0, 0.05);
  background-color: #ffffff;
  border: 1px solid #cfd1d3;
  text-align: center;
  vertical-align: middle;
  line-height: 30px;
  padding-top: 6px;
  margin-bottom: 10px;
  border-radius: 4px;
}
.category-body {
  display: block;
  width: 90%;
  margin-bottom: 16px;
  margin-left: 5px;
  padding-top: 5px;
  padding-right: 12px;
  font-weight: bold;
}
.dashboard-box2-left {
  padding: 3px 10px 0 5px;
  width: 40px;
  height: 100px;
  float: left;
  clear: none;
}
.big-digits-number2 {
  font-size: 28px;
  font-weight: bold;
}
.dashboard-box2-right {
  padding: 3px 38px 0 5px;
  width: 40px;
  float: right;
  clear: none;
}
.sentiment-icons2 img {
  width: 30px;
  position: absolute;
  float: right;
}
.box-link a {
  font-weight: bold;
  font-size: 21px;
}
.strategic-action {
  padding: 10px 10px 10px 20px;
  margin: 0 3px 0 10px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05), 0 5px 15px 0 rgba(0, 0, 0, 0.05);
  border: 1px solid #cfd1d3;
  border-radius: 4px;
}
/* unvisited link */
.box-link a:link {
  color: #2c3e50;
}
/* visited link */
.box-link a:visited {
  color: #2c3e50;
}
/* mouse over link */
.box-link a:hover {
  color: #2c3e50;
}
/* selected link */
.box-link a:active {
  color: #2c3e50;
}
.positive-color {
  background-color: #33a878;
}
.negative-color {
  background-color: #df6358;
}
.neutral-color {
  background-color: #e7e5e0;
}
.empty-color {
  background-color: #e4e3e1;
}
.feedback-item {
  lost-column: 1/2;
}

@media (max-width: 950px) {
  .ngram {
    lost-column: 1/2;
  }
  .dashboard-box {
    lost-column: 1/2;
  }
}
@media (max-width: 870px) {
  .feedback-item {
    lost-column: 1/1;
  }
}
@media (max-width: 663px) {
  .dashboard-box {
    lost-column: 1/1;
  }
}
@media (max-width: 480px) {
  .full-box {
    lost-utility: clearfix;
  }
  h1 {
    font-size: 20px;
  }
}
</style>
